import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import type { EntryLocationPin } from '../lib/api';

// Same Leaflet icon fix as LocationPicker.tsx
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export function MapPage() {
  const { t } = useTranslation();
  const [pins, setPins] = useState<EntryLocationPin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    api.getTags()
      .then((data) => setAllTags(data.tags))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasLoadedOnce.current) setIsLoading(true);
    const params: Record<string, string> = {};
    if (selectedTags.length > 0) params.tags = selectedTags.join(',');
    api.getEntryLocations(params)
      .then((data) => setPins(data.pins))
      .finally(() => {
        hasLoadedOnce.current = true;
        setIsLoading(false);
      });
  }, [selectedTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const defaultCenter: [number, number] = pins.length > 0
    ? [pins[0].locationLatitude, pins[0].locationLongitude]
    : [20, 0];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-ink">{t('nav.map')}</h1>
      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {allTags.map((tag) => {
            const isSelected = selectedTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`rounded-sm border px-2 py-0.5 text-xs transition-colors ${
                  isSelected
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-paper-300 bg-paper-200 text-ink-600 hover:border-paper-400'
                }`}
              >
                {tag}
              </button>
            );
          })}
          {selectedTags.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedTags([])}
              className="text-xs text-primary-600 hover:underline"
            >
              {t('entries.clearTagFilter')}
            </button>
          )}
        </div>
      )}
      {pins.length === 0 ? (
        <p className="text-ink-500 text-sm">
          {selectedTags.length > 0 ? t('map.noLocationsForTags') : t('map.noLocations')}
        </p>
      ) : (
        <div className="isolate overflow-hidden rounded border border-paper-300" style={{ height: '75vh' }}>
          <MapContainer center={defaultCenter} zoom={3} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap contributors"
            />
            {pins.map((pin) => (
              <Marker key={pin.id} position={[pin.locationLatitude, pin.locationLongitude]}>
                <Popup>
                  <div className="text-sm">
                    {pin.locationName && <p className="font-medium">{pin.locationName}</p>}
                    {pin.preview && <p className="text-ink-500">{pin.preview}…</p>}
                    <Link to={`/entries/${pin.id}/view`} className="text-primary-600 hover:underline">
                      {t('map.viewEntry')}
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
}
